import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Guest List Controller
// Handles bulk operations, selection, drag & drop reordering, expand/collapse, and kebab menus
export default class extends Controller {
  static targets = [
    "guestCheckbox",
    "bulkActionsBar",
    "selectedCount",
    "selectAllCheckbox",
    "selectAllLabel",
    "guestsList",
    "detailPanel"
  ]

  connect() {
    this.selectedGuests = new Set()
    this.setupDragAndDrop()
    this.handleFrameLoad = () => {
      this.selectedGuests = new Set()
      this.updateBulkActions()
      this.initializeSortable()
    }
    this.element.addEventListener('turbo:frame-load', this.handleFrameLoad)
  }

  disconnect() {
    if (this.handleFrameLoad) {
      this.element.removeEventListener('turbo:frame-load', this.handleFrameLoad)
    }
    if (this.activeKebabCloseHandler) {
      window.removeEventListener('click', this.activeKebabCloseHandler)
    }
  }

  // Handle individual guest selection
  toggleGuest(event) {
    const checkbox = event.target
    const guestId = checkbox.dataset.guestId

    if (checkbox.checked) {
      this.selectedGuests.add(guestId)
    } else {
      this.selectedGuests.delete(guestId)
    }

    this.updateBulkActions()
  }

  // Select/deselect all guests
  toggleAll(event) {
    const checked = event.target.checked
    this.guestCheckboxTargets.forEach(cb => {
      cb.checked = checked
      checked ? this.selectedGuests.add(cb.dataset.guestId) : this.selectedGuests.delete(cb.dataset.guestId)
    })
    this.updateBulkActions()
  }

  // Update bulk actions bar visibility and count
  updateBulkActions() {
    const count = this.selectedGuests.size

    if (count > 0) {
      this.bulkActionsBarTarget.classList.remove("hidden")
      this.selectedCountTarget.textContent = `${count} selected`
    } else {
      this.bulkActionsBarTarget.classList.add("hidden")
    }

    if (this.hasSelectAllCheckboxTarget) {
      const total = this.guestCheckboxTargets.length
      this.selectAllCheckboxTarget.checked = count === total && total > 0
      this.selectAllCheckboxTarget.indeterminate = count > 0 && count < total
      if (this.hasSelectAllLabelTarget) {
        this.selectAllLabelTarget.textContent = count > 0
          ? `${count} of ${total} selected`
          : `${total} ${total === 1 ? 'guest' : 'guests'}`
      }
    }
  }

  // Inline bulk action buttons
  bulkAction({ params: { bulkAction: action } }) {
    const guestIds = Array.from(this.selectedGuests)

    if (!action || guestIds.length === 0) return

    if (action === 'delete') {
      const count = guestIds.length
      if (!confirm(`Remove ${count} guest${count === 1 ? '' : 's'} from your guest list?`)) return
    }

    this.submitSelection(this.data.get('bulkUpdateUrl'), guestIds, { bulk_action: action })
  }

  // Move the selected guests between the reserve list and the invite list
  moveInviteStatus({ params: { inviteStatus } }) {
    const guestIds = Array.from(this.selectedGuests)
    if (!inviteStatus || guestIds.length === 0) return

    this.submitSelection(this.data.get('inviteStatusUrl'), guestIds, { _method: 'patch', invite_status: inviteStatus })
    this.clearSelection()
  }

  // Mark the selected guests' invites as sent (or undo that)
  markInvitesSent({ params: { sent } }) {
    const guestIds = Array.from(this.selectedGuests)
    if (guestIds.length === 0) return

    this.submitSelection(this.data.get('sentInvitationsUrl'), guestIds, sent ? {} : { _method: 'delete' })
    this.clearSelection()
  }

  // Submits the selected guest ids plus `fields` as a Turbo form post
  submitSelection(url, guestIds, fields) {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = url

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    guestIds.forEach(id => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'guest_ids[]'
      input.value = id
      form.appendChild(input)
    })

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value
      form.appendChild(input)
    })

    document.body.appendChild(form)
    form.requestSubmit()
  }

  // Cancel bulk selection
  cancelBulkSelection() {
    this.clearSelection()
  }

  // Clear all selections
  clearSelection() {
    this.selectedGuests.clear()
    this.guestCheckboxTargets.forEach(checkbox => {
      checkbox.checked = false
    })
    this.bulkActionsBarTarget.classList.add("hidden")
    if (this.hasSelectAllCheckboxTarget) {
      this.selectAllCheckboxTarget.checked = false
      this.selectAllCheckboxTarget.indeterminate = false
    }
    this.updateBulkActions()
  }

  // Toggle expandable detail panel
  toggleExpand(event) {
    event.stopPropagation()
    const guestId = event.currentTarget.dataset.guestId
    const panel = this.detailPanelTargets.find(p => p.dataset.guestPanelId === guestId)
    if (!panel) return

    panel.classList.toggle("hidden")

    const chevron = event.currentTarget.querySelector('[data-chevron]')
    chevron?.classList.toggle("rotate-180")
  }

  // Toggle kebab dropdown menu
  toggleKebab(event) {
    event.stopPropagation()
    const menu = event.currentTarget.nextElementSibling
    const isOpen = !menu.classList.contains("hidden")

    // Close all open kebab menus first
    document.querySelectorAll('[data-kebab-menu]').forEach(m => m.classList.add("hidden"))

    if (!isOpen) {
      menu.classList.remove("hidden")

      const closeHandler = (e) => {
        if (!menu.contains(e.target)) {
          menu.classList.add("hidden")
          window.removeEventListener('click', closeHandler)
          this.activeKebabCloseHandler = null
        }
      }
      this.activeKebabCloseHandler = closeHandler
      window.addEventListener('click', closeHandler)
    }
  }

  // Setup drag and drop for reordering
  setupDragAndDrop() {
    if (!this.hasGuestsListTarget) {
      return
    }
    this.initializeSortable()
  }

  initializeSortable() {
    try {
      Sortable.create(this.guestsListTarget, {
        handle: '.drag-handle',
        animation: 150,
        ghostClass: 'opacity-50',
        chosenClass: 'bg-blue-50',
        dragClass: 'rotate-2',

        onEnd: (evt) => {
          this.updateGuestOrder(evt)
        }
      })
    } catch (error) {
      console.log("SortableJS initialization failed:", error)
    }
  }

  // Update guest order after drag and drop
  updateGuestOrder(evt) {
    const guestIds = Array.from(this.guestsListTarget.children).map(item =>
      item.dataset.guestId
    ).filter(id => id)

    if (guestIds.length === 0) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.data.get('reorderUrl'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ guest_ids: guestIds })
    })
    .catch(error => {
      console.error('Error updating guest order:', error)
    })
  }
}
